import React, { useEffect, useMemo, useState } from 'react';

// Proste formatowanie imienia/nazwiska: trim + Pierwsza wielka, reszta małe
const formatName = (str: string): string => {
  if (!str) return '';
  const trimmed = str.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

interface PayUPaymentProps {
  /**
   * Cel darowizny – np. imię konkretnego zwierzaka.
   * Jeśli przekazany, opis transakcji będzie:
   * "Darowizna dla podopiecznego: {donationTarget}"
   * Jeśli nie, użyty zostanie opis domyślny:
   * "Darowizna na cele statutowe".
   */
  donationTarget?: string;
  /**
   * URL zdjęcia podopiecznego wyświetlanego w nagłówku formularza.
   * Jeśli nie zostanie przekazany, użyte zostanie zdjęcie domyślne.
   */
  petImageUrl?: string;
  /**
   * Czy ukryć zdjęcie podopiecznego nad formularzem.
   * Domyślnie false – zdjęcie jest widoczne.
   */
  hideImage?: boolean;
}

const PayUPayment: React.FC<PayUPaymentProps> = ({ donationTarget, petImageUrl, hideImage = false }) => {
  const [amount, setAmount] = useState('100');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [newsletterConsent, setNewsletterConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isHighContrastMode, setIsHighContrastMode] = useState(false);

  const PAYU_LOGO_LIGHT =
    'https://poland.payu.com/wp-content/themes/global-website/assets/img/PAYU_GPO-dark%20green.svg';
  const PAYU_LOGO_DARK = 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/PayU.svg/330px-PayU.svg.png';

  useEffect(() => {
    const getRootClasses = () => {
      const html = document.documentElement;
      const body = document.body;
      return {
        dark: Boolean(html?.classList?.contains('dark') || body?.classList?.contains('dark')),
        highContrast: Boolean(html?.classList?.contains('high-contrast') || body?.classList?.contains('high-contrast')),
      };
    };

    const updateThemeFlags = () => {
      const { dark, highContrast } = getRootClasses();
      setIsDarkMode(dark);
      setIsHighContrastMode(highContrast);
    };

    updateThemeFlags();

    const observer = new MutationObserver(updateThemeFlags);
    // app zwykle przełącza klasę na <html>, ale na wszelki wypadek obserwujemy też <body>
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (document.body) observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const payuLogoSrc = useMemo(() => {
    // HC traktujemy jak "ciemny" (tło jest bardzo ciemne)
    if (isHighContrastMode) return PAYU_LOGO_DARK;
    return isDarkMode ? PAYU_LOGO_DARK : PAYU_LOGO_LIGHT;
  }, [isDarkMode, isHighContrastMode]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string to clear the input
    if (value === '') {
      setAmount('');
      return;
    }
    // Use regex to allow only positive integers
    if (/^[1-9]\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    setLoading(true);
    setError(null);

    const trimmedTarget = donationTarget?.trim();
    const description = trimmedTarget
      ? `Darowizna dla podopiecznego: ${trimmedTarget}`
      : 'Darowizna na cele statutowe';

    const sanitizedData = {
      amount: parseInt(amount, 10),
      firstName: formatName(firstName),
      lastName: formatName(lastName),
      email: email.trim().toLowerCase(),
      description,
    };

    try {
      const response = await fetch('/.netlify/functions/payu-create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData),
      });

      const data = await response.json();

      if (response.ok && data.redirectUri) {
        // --- 1. ZAPISUJEMY ID ZAMÓWIENIA ---
        let payuOrderId = data.orderId;
        
        // Awaryjne wyciąganie ID z linku, jeśli backend nie zwrócił wprost
        if (!payuOrderId && data.redirectUri.includes('orderId=')) {
           const urlObj = new URL(data.redirectUri);
           payuOrderId = urlObj.searchParams.get('orderId');
        }

        if (payuOrderId) {
            console.log('📦 Zapisuję ID PayU do schowka:', payuOrderId);
            localStorage.setItem('lastPayuOrderId', payuOrderId);
        }

        // --- 2. ZAPISUJEMY EMAIL (DLA MAILCHIMP) ---
        if (sanitizedData.email) {
            console.log('📧 Zapisuję Email do schowka:', sanitizedData.email);
            localStorage.setItem('lastPayuEmail', sanitizedData.email);
        }

        // --- 3. ZAPISUJEMY ZGODĘ NA NEWSLETTER ---
        localStorage.setItem('lastPayuNewsletterConsent', newsletterConsent.toString());

        // --- 3. PRZEKIEROWANIE ---
        window.location.href = data.redirectUri;
      } else {
        setError(data.error || 'Wystąpił błąd podczas tworzenia płatności.');
      }
    } catch (err) {
      setError('Nie można połączyć się z serwerem. Spróbuj ponownie później.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
      <div className="mb-4">
        <img 
          src={payuLogoSrc}
          alt="Logo PayU" 
          className="h-8 md:h-10 w-auto"
        />
      </div>
      {!hideImage && (
        <div className="mb-6">
          <img
            src={petImageUrl || '/photos/IMG_5075.webp'}
            alt={donationTarget ? `Podopieczny ${donationTarget}` : 'Podopieczny fundacji'}
            loading="lazy"
            className="w-full h-44 sm:h-56 md:h-64 object-cover object-center rounded-lg"
          />
        </div>
      )}
      {/* <h2 className="heading-3 mb-2">Wpłata przez PayU</h2> */}
      {donationTarget && donationTarget.trim() && (
        <p className="body-medium mb-4">
          Wspierasz podopiecznego: <span className="font-semibold">{donationTarget.trim()}</span>
        </p>
      )}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="amount" className="label-medium mb-2">
            Kwota (PLN)
          </label>

          {/* Kafelki z kwotami */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
            {[5, 10, 20, 50, 100].map((value) => {
              const isActive = Number(amount) === value;

              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAmount(String(value))}
                  className={
                    'py-2 rounded-lg text-sm font-medium transition-colors border ' +
                    (isActive
                      ? 'bg-accessible-red-600 text-white border-accessible-red-600'
                      : 'border-accessible-red-600 text-accessible-red-600 hover:bg-accessible-red-600/5')
                  }
                >
                  {value} zł
                </button>
              );
            })}
          </div>

          {/* Ręczny input kwoty, zsynchronizowany z kafelkami */}
          <div className="relative">
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={handleAmountChange}
              min={1}
              max={1000}
              step={1}
              className="w-full p-2 border rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-white dark:bg-accessible-gray-700 dark:border-accessible-gray-600 dark:text-white high-contrast:bg-accessible-gray-900 high-contrast:border-white high-contrast:text-white"
              required
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
              PLN
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="firstName" className="label-medium mb-1">
              Imię
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={50}
              className="w-full p-2 border rounded bg-white dark:bg-accessible-gray-700 dark:border-accessible-gray-600 dark:text-white high-contrast:bg-accessible-gray-900 high-contrast:border-white high-contrast:text-white"
              required
            />
          </div>
          <div>
            <label htmlFor="lastName" className="label-medium mb-1">
              Nazwisko
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={50}
              className="w-full p-2 border rounded bg-white dark:bg-accessible-gray-700 dark:border-accessible-gray-600 dark:text-white high-contrast:bg-accessible-gray-900 high-contrast:border-white high-contrast:text-white"
              required
            />
          </div>
        </div>
        <div className="mb-6">
          <label htmlFor="email" className="label-medium mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-accessible-gray-700 dark:border-accessible-gray-600 dark:text-white high-contrast:bg-accessible-gray-900 high-contrast:border-white high-contrast:text-white"
            required
          />
        </div>

        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={newsletterConsent}
              onChange={(e) => setNewsletterConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-accessible-gray-300 text-accessible-red-600 focus:ring-accessible-red-600"
            />
            <span className="text-sm text-accessible-gray-700 dark:text-accessible-gray-200">
              Wyrażam zgodę na otrzymywanie newslettera z informacjami o działalności fundacji 
              (obsługiwanego przez Mailchimp). W każdej chwili mogę zrezygnować z subskrypcji.
            </span>
          </label>
          <p className="text-xs text-accessible-gray-600 dark:text-accessible-gray-300 mt-3 ml-7">
            Dokonując płatności, akceptujesz{' '}
            <a
              href="/regulamin"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-accessible-red-600"
            >
              Regulamin
            </a>
            {' '}i{' '}
            <a
              href="/polityka-prywatnosci"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-accessible-red-600"
            >
              Politykę Prywatności
            </a>
            . Twoje dane będą przetwarzane zgodnie z RODO.
          </p>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors font-semibold disabled:bg-accessible-gray-400"
        >
          {loading ? 'Przetwarzanie...' : `Wpłać ${amount} PLN`}
        </button>
        {error && <p className="text-red-500 mt-4">{error}</p>}
      </form>
    </div>
  );
};

export default PayUPayment;